<template>
	<AppBottomSheet :active="active" :forceWidth="bottomSheetWidth" @toggling-popup="open => $emit('toggling-popup', open)" >
		<div class="customers-people-add-container" v-if="active">
			<ModalHeader
				:title="getString('new_company')"
				:button-text="getString('add')"
				@click-button="addCustomer"
			/>
			<div class="row inputs-containers">
				<div v-if="showButtonExisting" class="wrapper-buttons-add">
					<div class="row-button-add">
						<AppButton icon-left="icon-pointer-hand" small outline :text="getString('existing_company')" @click="closeBottomSheetCompany" />
					</div>
				</div>
				<CompanyInfoCollector allow-no-vat ref="company-info" @changed-company="changedCompany" />
			</div>
		</div>
		<AppPopup
			:title="getString('same_vat_fiscal_error_title')"
			:body-text="getString('same_vat_fiscal_error_text', [this.company?.vat])"
			:buttons="popupVatFiscalButtons"
			ref="confirm-same-vat-fiscal"
		/>
	</AppBottomSheet>
</template>

<script>
import AppTextInput from "@/shared/components/AppTextInput.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import AppLoader from "@/shared/components/AppLoader.vue";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import AppDropdown from "@/shared/components/AppDropdown.vue";
import AppList from "@/shared/components/AppList.vue";
import AppGoogleMapsInput from "@/shared/components/AppGoogleMapsInput.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import {useLanguageStore, useNotificationStore} from "@/shared/stores";
import {useCustomersStore, useUserStore} from "@/console-new/stores";
import {validateEmail} from "@/shared/helpers/strings";
import API from "@/shared/helpers/API";
import AppButton from "@/shared/components/AppButton.vue";
import CompanyInfoCollector from "@/console-new/components/shared/CompanyInfoCollector.vue";
import AppPopup from "@/shared/components/AppPopup.vue";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const userStore = useUserStore();
		const notificationStore = useNotificationStore();
		const customersStore = useCustomersStore();
		return {getString: languageStore.getString, showNotification: notificationStore.showNotification, userStore, customersStore}
	},
	name: "CustomersCompanyAdd",
	components: {AppPopup, AppButton, AppGoogleMapsInput, ModalHeader, CompanyInfoCollector, AppLoader, AppDropdown, AppList, AppBottomSheet, AppTextInput},
	mixins: [WindowWidth],
	emits: ['toggling-popup', 'added-customer', 'close'],
	props: {
		active: {
			type: Boolean,
			default: false,
		},
		showButtonExisting: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			company: {
				country_code: null,
				vat: null,
				fiscal_name: null,
				fiscal_code: null,
				legal_address: null,
				delivery_address: null,
				sdi: null,
				pec: null,
				notes: null,
			},
			collapsedDropdown: true,
			loadingVat: false,
			dismissedSameVatFiscal: false,
			companyWithoutVat: false,
		}
	},
	computed: {
		popupVatFiscalButtons() {
			return [
				{text: this.getString('edit'), callback: null, negative: true},
				{text: this.getString('confirm'), callback: this.confirmSameVatFiscal},
			]
		},
		bottomSheetWidth() {
			if (this.windowWidth < 550) {
				return "100%";
			} else if (this.windowWidth < 768) {
				return "550px";
			} else {
				return "720px";
			}
		},
	},
	methods: {
		changedCompany(newCompany, withoutVat = false) {
			this.company = newCompany;
			this.companyWithoutVat = !!withoutVat;
		},
		resetCompany() {
			this.company = {
				country_code: null,
				vat: null,
				fiscal_name: null,
				fiscal_code: null,
				legal_address: null,
				delivery_address: null,
				sdi: null,
				pec: null,
				notes: null,
			}
			this.$refs['company-info'].resetCompany();
			this.dismissedSameVatFiscal = false;
		},
		confirmSameVatFiscal() {
			this.dismissedSameVatFiscal = true;
			this.$refs['confirm-same-vat-fiscal'].toggleStatus(false);
			this.addCustomer();
		},
		addCustomer() {

			let vat = this.company.vat;
			let finalCountry = this.company?.country_code || COUNTRY;
			if(finalCountry === 'IT') {
				vat = vat && vat.length >= 11 ? vat : null;
				if(vat && vat.startsWith("IT")) {
					vat = vat.substring(2);
				}
				if((!vat || vat.length !== 11) && !this.companyWithoutVat) {
					this.showNotification("invoice-vatid-invalid");
					return;
				}
			} else if((!vat || vat.length > 28) && !this.companyWithoutVat) {
				this.showNotification("invoice-vatid-invalid");
				return;
			}

			let name = this.company.fiscal_name;
			if(!name || name.length <= 2) {
				this.showNotification("name-required");
				return;
			}

			let sdi = this.company.sdi;
			if(sdi && sdi?.length) {
				if(sdi.length !== 7) {
					this.showNotification("invoice-sdi-invalid");
					return;
				}
			} else {
				this.company.sdi = sdi = finalCountry === 'IT' ? '0000000' : 'XXXXXXX';
			}

			let pec = this.company.pec;
			pec = pec && pec.length > 0 ? pec : null;
			if(pec && !validateEmail(pec)) {
				this.showNotification("wrong-email");
				return;
			}

			let fiscal = this.company.fiscal_code;
			fiscal = fiscal && fiscal.length > 0 ? fiscal : null;
			if((fiscal && fiscal.length < 11) || (!fiscal && this.companyWithoutVat)) {
				this.showNotification("wrong-fiscal");
				return;
			}

			let notes = this.company.notes;
			notes = notes && notes.length > 0 ? notes : null;

			if(!this.company.legal_address) {
				this.showNotification("invoice-address-invalid");
				return;
			}

			let legalAddress = JSON.stringify(this.company.legal_address);
			let deliveryAddress = this.company.delivery_address ? JSON.stringify(this.company.delivery_address) : null;

			if(!this.dismissedSameVatFiscal && vat && fiscal && vat === fiscal) {
				this.$refs['confirm-same-vat-fiscal'].toggleStatus(true);
				return;
			}
			this.dismissedSameVatFiscal = false;

			let privacy = this.company?.privacy;

			API.init().addCustomerCompany(this.userStore.currBusinessId, null, name, vat, this.company?.country_code, fiscal, sdi, legalAddress, deliveryAddress, pec, notes, privacy, (response) => {
				if(response && response.result && response.result === 'OK' && response?.customer && response.customer?.customer_id) {
					this.showNotification("generic-success");
					let normalizedCustomer = this.customersStore.normalizeCustomerCompany(response.customer);
					this.$emit('added-customer', normalizedCustomer, name);
					this.resetCompany();
				} else {
					this.showNotification("generic-fail");
				}
			});
		},
		closeBottomSheetCompany() {
			this.$emit('close');
		},
	}
}
</script>
<style lang="scss">
.pac-container {
	border-radius: 13px;
	border-top: none;
	background: transparent;

	&.pac-logo:after {
		display: none;
	}

	.pac-item {
		padding: 0 8px;
		background: var(--color-bg);
		border-color: var(--color-border-alpha);

		&:first-child {
			border-top-left-radius: 13px;
			border-top-right-radius: 13px;
			border-top: none;
		}

		.pac-icon-marker {
			display: none;
		}

		span {
			font-size: var(--font-size-s);
			line-height: var(--line-height-s);
			color: var(--color-txt);
		}

		&.pac-item-selected {
			background: var(--color-bg);
		}
	}

}
</style>
<style scoped lang="scss">
.vat-dropdown-container {
	width: 100%;
	padding: 0 10px;
}
:deep(.dropdown-container) {
	.dropdown .dropdown-content {
		margin-top: 0;
		padding: 0;
		border-radius: 0 0 13px 13px;
	}
}
:deep(.list-container) {
	.list-item {
		//padding: 10px 14px;
	}
}
.inputs-containers {
	display: flex;
	flex-direction: column;
	align-items: flex-end;

	.input-container {
		width: 100%;
		display: flex;
		flex-wrap: wrap;
		gap: 12px;
		justify-content: space-between;
		align-items: center;
		position: relative;
		padding: 8px 12px;

		.ml-auto {
			margin-left: auto;
		}

		&:after {
			content: "";
			position: absolute;
			top: 100%;
			left: 0;
			height: 1px;
			width: 100%;
			background-color: var(--color-alpha-3);
		}

		&:last-child {
			&:after {
				display: none;
			}
		}

		&.vat-input-container {
			border: 1px solid var(--color-primary);
			border-radius: 13px;
			background-color: var(--color-bg-primary-2);

			.grey {
				font-size: 14px;
				color: var(--color-txt-2);
			}
			&:after {
				display: none;
			}

			.vat-input {
				max-width: calc(100% - 32px) !important;
			}

		}
	}
}
.list-item-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-wrap: wrap;
	width: 100%;
	gap: 8px;

	.right-item {
		margin-left: auto;
	}
	&:deep(.map-input-container){
		.second-row {
			justify-content: flex-end;
			width: 100%;
			max-width: 280px;
		}
		.input-wrapper .input-optional {
			width: 100px;
		}
	}
	&:deep(.maps-input-wrapper){
		&.text-wrapper {
			width: 240px;
			.address-input {
				width: 100%;
			}
		}

		@media (max-width: 1250px) {
			&.text-wrapper {
				width: 190px;
				max-width: 190px;
			}
		}
	}
}
.wrapper-buttons-add {
	height: 100%;
	width: 100%;
	margin-bottom: 10px;
	.row-button-add {
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		align-items: center;
		width: 100%;
	}
	.base-button {
		height: 40px;
		width: 100%;
		max-width: unset;
		background-color: var(--color-bg-primary-2);
		border: none;
		&:hover {
			box-shadow: none;
		}
	}
}
</style>